/**
 * Console Capture Hook
 *
 * Intercepts console.error and console.warn to capture errors for bug reports
 * Uses singleton pattern - safe to call from multiple components
 */

import { useEffect } from 'react'
import type { ConsoleError } from '../types/feedback.types'

const MAX_ERRORS = 5

// Module-level singleton state (shared across all hook instances)
let errorsBuffer: ConsoleError[] = []
let isInitialized = false
let originalError: typeof console.error | null = null
let originalWarn: typeof console.warn | null = null

/**
 * Initialize console capture (call once at app level)
 */
function initCapture() {
  if (isInitialized) return

  // Store original console methods
  originalError = console.error
  originalWarn = console.warn

  // Override console.error
  console.error = (...args: unknown[]) => {
    const message = args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ')

    const error: ConsoleError = {
      type: 'error',
      message,
      timestamp: new Date().toISOString(),
      stack: new Error().stack
    }

    // Keep only last MAX_ERRORS
    errorsBuffer = [...errorsBuffer, error].slice(-MAX_ERRORS)

    // Call original console.error
    originalError?.apply(console, args)
  }

  // Override console.warn
  console.warn = (...args: unknown[]) => {
    const message = args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ')

    const error: ConsoleError = {
      type: 'warn',
      message,
      timestamp: new Date().toISOString()
    }

    // Keep only last MAX_ERRORS
    errorsBuffer = [...errorsBuffer, error].slice(-MAX_ERRORS)

    // Call original console.warn
    originalWarn?.apply(console, args)
  }

  isInitialized = true
}

/**
 * Console capture hook - singleton pattern
 * Safe to call from multiple components
 *
 * NOTE: This hook intentionally does NOT restore original console methods on cleanup.
 * This is by design - console capture should persist for the lifetime of the app
 * to capture errors that occur between bug report submissions. The singleton pattern
 * ensures console methods are only overridden once, regardless of how many components
 * use this hook.
 */
export function useConsoleCapture() {
  useEffect(() => {
    initCapture()
    // No cleanup intentional - singleton pattern keeps capture active app-wide
  }, [])

  const getErrors = () => errorsBuffer

  const clearErrors = () => {
    errorsBuffer = []
  }

  return {
    getErrors,
    clearErrors
  }
}
